import React, { useState } from 'react'

import { Box, SortDirection, Table, Text } from '@factorialco/deprecated-design-system'
import { F0TagStatus, StatusVariant, VerticalBarChart } from '@factorialco/f0-react'
import { ExpensesExpensablesByApprovalStatus } from 'generated/resources/graphql'
import _ from 'lodash'

import i18n from 'modules/core/lib/i18n'

import Money from '../Money'

interface ExpenseAmountByStatusTableProps {
  currentPeriod?: ExpensesExpensablesByApprovalStatus
  currency: string
}

interface StatusData {
  id: string
  title: string
  labelVariant: StatusVariant
  totalAmount: number
  totalCount: number
}

const ExpenseAmountByStatusTable: React.FC<ExpenseAmountByStatusTableProps> = ({
  currentPeriod,
  currency,
}) => {
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [sortAttribute, setSortAttribute] = useState<string>('status')

  const sortStatusClick = () => {
    const newSortDirection = sortDirection === 'desc' ? 'asc' : 'desc'

    setSortDirection(newSortDirection)
    setSortAttribute('status')
  }

  const sortTotalAmountClick = () => {
    const newSortDirection = sortDirection === 'desc' ? 'asc' : 'desc'

    setSortDirection(newSortDirection)
    setSortAttribute('totalAmount')
  }

  const statusData: StatusData[] = [
    {
      id: 'pending',
      title: i18n.t('expenses_v2.reports.expenses_status_funnel.column.pending'),
      labelVariant: 'warning',
      totalAmount: currentPeriod?.pending.totalAmount ?? 0,
      totalCount: currentPeriod?.pending.totalCount ?? 0,
    },
    {
      id: 'approved',
      title: i18n.t('expenses_v2.reports.expenses_status_funnel.column.approved'),
      labelVariant: 'info',
      totalAmount: currentPeriod?.approved.totalAmount ?? 0,
      totalCount: currentPeriod?.approved.totalCount ?? 0,
    },
    {
      id: 'inPayroll',
      title: i18n.t('expenses_v2.reports.expenses_status_funnel.column.in_payroll'),
      labelVariant: 'positive',
      totalAmount: currentPeriod?.inPayroll.totalAmount ?? 0,
      totalCount: currentPeriod?.inPayroll.totalCount ?? 0,
    },
    {
      id: 'sentToPay',
      title: i18n.t('expenses_v2.reports.expenses_status_funnel.column.sent_to_pay'),
      labelVariant: 'neutral',
      totalAmount: currentPeriod?.sentToPay.totalAmount ?? 0,
      totalCount: currentPeriod?.sentToPay.totalCount ?? 0,
    },
    {
      id: 'paid',
      title: i18n.t('expenses_v2.reports.expenses_status_funnel.column.paid'),
      labelVariant: 'info',
      totalAmount: currentPeriod?.paid.totalAmount ?? 0,
      totalCount: currentPeriod?.paid.totalCount ?? 0,
    },
  ]

  const maxCount =
    _.max(Object.entries(statusData).map(([_status, object]) => object?.totalCount || 0)) || 0

  const sortedData = statusData.sort((a, b) => {
    if (sortAttribute === 'status') {
      if (sortDirection === 'asc') {
        return a.title < b.title ? -1 : 1
      } else {
        return a.title > b.title ? -1 : 1
      }
    } else {
      if (sortDirection === 'asc') {
        return a.totalAmount < b.totalAmount ? -1 : 1
      } else {
        return a.totalAmount > b.totalAmount ? -1 : 1
      }
    }
  })

  return (
    <Table hideBorder fullWidth autoLayout>
      <Table.Row background="grey000White">
        <Table.HeadCell
          background="grey000White"
          noBorder
          style={{
            borderBottom: 'none',
            paddingLeft: '0',
            paddingRight: '16px',
            width: '1%',
            whiteSpace: 'nowrap',
          }}
        >
          <Table.SortingHeader
            onClick={sortStatusClick}
            selected={sortAttribute === 'status' ? sortDirection == 'asc' : null}
            justifyContent={'flexStart'}
          >
            {i18n.t('expenses_v2.reports.expenses_status_funnel.headers.status')}
          </Table.SortingHeader>
        </Table.HeadCell>
        <Table.HeadCell
          background="grey000White"
          noBorder
          style={{
            borderBottom: 'none',
            paddingLeft: '16px',
            paddingRight: '0',
            width: '1%',
            whiteSpace: 'nowrap',
          }}
        >
          <Table.SortingHeader
            onClick={sortTotalAmountClick}
            selected={sortAttribute === 'totalAmount' ? sortDirection == 'asc' : null}
            justifyContent={'flexEnd'}
          >
            {i18n.t('expenses_v2.reports.expenses_status_funnel.headers.expenses')}
          </Table.SortingHeader>
        </Table.HeadCell>
        <Table.HeadCell
          background="grey000White"
          noBorder
          style={{ borderBottom: 'none', width: 'auto', paddingLeft: '0', paddingRight: '24px' }}
        ></Table.HeadCell>
      </Table.Row>

      {sortedData.map((status) => (
        <Table.Row key={status.id}>
          <Table.Cell
            paddingY="s8"
            style={{
              paddingLeft: '0',
              paddingRight: '16px',
              width: '1%',
              whiteSpace: 'nowrap',
            }}
            noBorder
          >
            <Box width="fit-content">
              <F0TagStatus text={status.title} variant={status.labelVariant} />
            </Box>
          </Table.Cell>
          <Table.Cell
            paddingY="s8"
            style={{
              paddingLeft: '16px',
              paddingRight: '0',
              width: '1%',
              whiteSpace: 'nowrap',
            }}
            noBorder
          >
            <Box textAlign="right">
              <Text.Normal size="200" color="textSecondary" weight="semibold">
                <Money amount={status.totalAmount ?? 0} currency={currency} />
              </Text.Normal>
            </Box>
          </Table.Cell>
          <Table.Cell
            paddingY="s0"
            noBorder
            style={{ width: 'auto', paddingLeft: '0', paddingRight: '24px' }}
          >
            <VerticalBarChart
              hideGrid
              data={[
                {
                  label: status.title,
                  values: {
                    totalCount: status.totalCount ?? 0,
                  },
                },
              ]}
              dataConfig={{
                totalCount: {
                  label: i18n.t('expenses_v2.reports.expenses_status_funnel.total'),
                },
              }}
              yAxis={{
                hide: true,
              }}
              xAxis={{
                hide: true,
                domain: [0, maxCount],
              }}
            />
          </Table.Cell>
        </Table.Row>
      ))}
    </Table>
  )
}
export default ExpenseAmountByStatusTable
